<template>
  <!-- 公共绑定辅料弹窗：支持多选辅料并编辑每套消耗数 -->
  <el-dialog class="material-dialog" :title="$t('message.bindingAuxiliaryMaterials')" :visible.sync="visible" width="1320px" append-to-body
    :close-on-click-modal="false">
    <!-- 辅料名称、订单号、分类搜索及重置 -->
    <div class="material-search">
      <el-input v-model.trim="searchName" :placeholder="$t('message.pleaseEnterMaterialName')" clearable
        @keyup.enter.native="search" />
      <el-input v-model.trim="orderSn" :placeholder="$t('message.orderNumber')" clearable
        @keyup.enter.native="search" />
      <el-select v-model="typeId" :placeholder="$t('message.materialType')" clearable filterable>
        <el-option v-for="item in typeList" :key="item.id" :label="item.name" :value="item.id" />
      </el-select>
      <el-button type="primary" :loading="loading" @click="search">{{ $t('message.search') }}</el-button>
      <el-button @click="reset">{{ $t('message.reset') }}</el-button>
    </div>

    <!-- 辅料列表：点击行或复选框切换选择，图片、名称和消耗编辑不触发行选择 -->
    <div class="material-list">
      <el-table v-loading="loading" :data="list" row-key="id" border max-height="500"
        :empty-text="$t('message.noMaterial')" :header-cell-style="{ background: '#EDEFF4' }" @row-click="toggle">
        <el-table-column align="center" width="54">
          <template slot-scope="scope">
            <el-checkbox class="material-checkbox" :value="isSelected(scope.row)" @click.native.stop @change="toggle(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column prop="id" :label="$t('message.materialId')" align="center" width="90" />
        <el-table-column :label="$t('message.mainImage')" align="center" width="100">
          <template slot-scope="scope">
            <div class="material-image" @click.stop>
              <el-image v-if="scope.row.main_image" :src="scope.row.main_image" fit="cover"
                :preview-src-list="[scope.row.main_image]" />
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.materialNameTrans')" min-width="180">
          <template slot-scope="scope">
            <div class="material-name" :title="scope.row.name_trans" @click.stop="openMaterialDetail(scope.row)">{{ scope.row.name_trans }}</div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.type')" min-width="130">
          <template slot-scope="scope">{{ scope.row.type.name }}</template>
        </el-table-column>
        <el-table-column :label="$t('message.status')" align="center" width="120">
          <template slot-scope="scope">
            <!-- 沿用调用页面的库存状态文案和颜色规则 -->
            <span v-if="showStatusName" class="stock-status" :class="statusNameClass(scope.row)">{{ scope.row.status_name }}</span>
            <span v-else class="stock-status" :class="stockStatusClass(scope.row)">{{ stockStatusText(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('message.inventoryQuantity')" align="center" width="110">
          <template slot-scope="scope">{{ scope.row.material_stock_num }}</template>
        </el-table-column>
        <el-table-column :label="$t('message.materialConsumption')" align="center" width="200">
          <template slot-scope="scope">
            <div class="consume-row" @click.stop>
              <el-input v-model="scope.row.consume" size="mini" />
              <span>{{ $t('message.pointsPerSet') }}</span>
            </div>
          </template>
        </el-table-column>
        <!-- 辅料所在订单沿用辅料列表字段；订单链接不触发行选择 -->
        <el-table-column :label="$t('message.materialInOrder')" min-width="260">
          <template slot-scope="scope">
            <div class="orders-cell">
              <div v-for="(order, orderIndex) in getOrderList(scope.row)" :key="orderIndex" class="order-card">
                <router-link :to="`/orderPaymentDetail?order_sn=${order.order_sn}`" target="_blank"
                  class="order-sn" @click.native.stop>
                  {{ order.order_sn }}
                </router-link>
                <div>
                  <span>{{ order.sorting }}{{ $t('message.mash') }}</span>
                  <span class="order-points">
                    {{ $t('message.remainingPoints') }}
                    <b>{{ order.send_await_num }}</b>
                    {{ $t('message.point') }}
                  </span>
                </div>
              </div>
              <div v-if="!getOrderList(scope.row).length">-</div>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 取消关闭弹窗；确认返回完整的已选辅料对象 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('message.cancel') }}</el-button>
      <el-button type="primary" @click="confirm">{{ $t('message.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * 公共绑定辅料弹窗。
 * 使用 warehouseApi.materialList 查询当前客户辅料；confirm 返回包含 material_id、consume 的完整辅料对象。
 */
export default {
  name: 'MaterialBindDialog',
  props: {
    // 当前客户 ID，辅料列表必须按客户筛选
    userId: {
      default: ''
    },
    showStatusName: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      searchName: '',
      orderSn: '', // 订单号筛选，直接传列表接口 order_sn
      typeId: '',
      typeList: [],
      list: [],
      selectedList: []
    }
  },
  methods: {
    // 打开弹窗；单个商品可传入当前绑定列表用于回显
    open(selectedList = []) {
      if (!this.userId) {
        this.$message.warning(this.$t('message.customerIdRequired'))
        return
      }
      this.searchName = ''
      this.orderSn = ''
      this.typeId = ''
      this.selectedList = selectedList.map(item => ({
        ...item,
        id: item.id || item.material_id
      }))
      this.visible = true
      this.materialTypeList()
      this.materialList()
    },
    // 分类数据与辅料列表保持一致，仅在打开弹窗时加载。
    materialTypeList() {
      this.$api.warehouseApi.materialTypeList().then(res => {
        this.typeList = res.data || []
      })
    },
    // 按辅料名称、订单号和分类查询当前客户的辅料
    search() {
      this.materialList()
    },
    // 清空名称、订单号和分类后重新加载辅料列表
    reset() {
      this.searchName = ''
      this.orderSn = ''
      this.typeId = ''
      this.materialList()
    },
    isOutOfStock(item) {
      return Number(item.material_stock_num || 0) === 0
    },
    isStockInsufficient(item) {
      return Number(item.material_stock_num || 0) < Number(item.pre_consume_num || 0)
    },
    stockStatusText(item) {
      if (this.isOutOfStock(item)) return this.$t('message.noStock')
      if (this.isStockInsufficient(item)) return this.$t('message.stockInsufficient')
      if (Number(item.pre_up_num || 0) > 0) return this.$t('message.suggestReplenishment')
      return this.$t('message.stockSufficient')
    },
    stockStatusClass(item) {
      if (this.isOutOfStock(item) || this.isStockInsufficient(item)) return 'is-danger'
      if (Number(item.pre_up_num || 0) > 0) return 'is-warning'
      return 'is-success'
    },
    // 付款后订单状态名沿用库存判断，但互换补货提示与库存充足的展示颜色。
    statusNameClass(item) {
      if (this.isOutOfStock(item) || this.isStockInsufficient(item)) return 'is-danger'
      if (Number(item.pre_up_num || 0) > 0) return 'is-success'
      return 'is-warning'
    },
    // 加载列表时保留已选辅料的消耗数及完整字段
    materialList() {
      this.loading = true
      this.$api.warehouseApi.materialList({
        user_id: this.userId,
        name: this.searchName,
        order_sn: this.orderSn,
        type_id: this.typeId,
        page: 1,
        pageSize: 999
      }).then(res => {
        const data = res.data || {}
        this.list = (data.list || data.data || []).map(item => {
          const selectedIndex = this.selectedList.findIndex(selected => selected.id === item.id)
          if (selectedIndex === -1) {
            return {
              ...item,
              consume: item.consume !== undefined && item.consume !== null ? item.consume : 1
            }
          }
          const selected = {
            ...item,
            consume: this.selectedList[selectedIndex].consume
          }
          this.selectedList.splice(selectedIndex, 1, selected)
          return selected
        })
      }).finally(() => {
        this.loading = false
      })
    },
    // 判断当前行是否已选，筛选后仍保留原选择
    isSelected(item) {
      return this.selectedList.some(selected => selected.id === item.id)
    },
    // 多选时保留完整列表对象，便于确认后直接回填商品辅料列表
    toggle(item) {
      const index = this.selectedList.findIndex(selected => selected.id === item.id)
      if (index === -1) {
        this.selectedList.push(item)
      } else {
        this.selectedList.splice(index, 1)
      }
    },
    // 直接返回已选辅料，并补充订单提交需要的 material_id
    confirm() {
      if (!this.selectedList.length) {
        this.$message.warning(this.$t('message.pleaseSelectMaterial'))
        return
      }
      const selectedList = this.selectedList.map(item => ({
        ...item,
        material_id: item.id,
        consume: item.consume
      }))
      this.$emit('confirm', selectedList)
      this.visible = false
    },
    // 与辅料列表一致，读取接口返回的辅料所在订单明细
    getOrderList(item) {
      if (Array.isArray(item.detail_list)) return item.detail_list
      if (Array.isArray(item.order_list)) return item.order_list
      return []
    },
    // 点击辅料名称新开辅料详情
    openMaterialDetail(item) {
      const id = item.id || item.material_id
      if (!id) return
      const route = this.$router.resolve({
        path: '/materialDetail',
        query: {
          id,
          user_id: this.userId || item.user_id || ''
        }
      })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
// 新增订单列后预留展示宽度，窄屏限制在视口内并由表格横向滚动
.material-dialog /deep/ .el-dialog {
  max-width: calc(100vw - 40px);
}

.material-search {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  > .el-input {
    width: 240px;
  }

  .el-select {
    width: 200px;
  }
}

// 列表沿用弹窗原有滚动高度，不改变确认按钮的位置
.material-list {
  min-height: 260px;
}

.material-image {
  width: 64px;
  height: 64px;
  margin: 0 auto;
  border-radius: 4px;
  background-color: #9b9b9b;
  overflow: hidden;

  .el-image {
    width: 100%;
    height: 100%;
  }
}

.material-name {
  color: #303133;
  line-height: 22px;
  word-break: break-all;
  cursor: pointer;

  &:hover {
    color: #c8383e;
  }
}

.stock-status {
  font-weight: bold;

  &.is-danger {
    color: #c8383e;
  }

  &.is-warning {
    color: #e6a23c;
  }

  &.is-success {
    color: #15ad3c;
  }
}

.consume-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  .el-input {
    width: 86px;
  }
}

// 选择列不继承业务页面的双倍缩放，去掉默认右边距，避免勾选框偏移和裁切
.material-checkbox {
  display: inline-flex;
  align-items: center;
  margin: 0;
  min-width: 0;
  min-height: 0;
  line-height: 1;
  vertical-align: middle;

  /deep/ .el-checkbox__inner {
    width: 20px;
    height: 20px;
    transform: none !important;

    &::after {
      top: 2px;
      left: 6px;
      width: 5px;
      height: 10px;
    }
  }
}

/deep/ .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #c8383e;
  background-color: #c8383e;
}

// 辅料所在订单沿用列表样式，长订单号可换行
.orders-cell {
  .order-card {
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f7f7f7;
    border-radius: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .order-sn {
    display: block;
    margin-bottom: 4px;
    color: #b4272d;
    font-weight: 600;
    text-decoration: underline;
    word-break: break-all;
  }

  .order-points {
    margin-left: 12px;

    b {
      color: #b4272d;
    }
  }
}

.dialog-footer {
  text-align: center;
}
</style>
